import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  ArrowLeft,
  Activity,
  Users,
  Link2,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Zap,
  BarChart2,
  Server,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  RefreshCw,
  Shield,
  Ban,
  Trash2,
  Search,
  GitBranch,
  ListTree,
  Plus,
  Loader2,
  Share2,
  Link2Off,
  GraduationCap,
  Wrench,
  User,
  Upload,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "../../../ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import { Input } from "../../../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { GatewayAgentsApi, GatewayProfilesApi, GatewayAuditApi, GatewayStsApi, GatewayA2AApi, GatewayHumanUsersApi, GatewayPostProcessorApi } from "../Api/gatewayApi";
import GatewayStatusBadge from "../components/GatewayStatusBadge";
import GatewayProtocolBadge from "../components/GatewayProtocolBadge";
import GatewayCapabilityTypeBadge from "../components/GatewayCapabilityTypeBadge";
import GatewaySensitivityBadge, { skillSensitivity } from "../components/GatewaySensitivityBadge";
import {
  formatTimestamp,
  timeAgo,
  callerTypeLabel,
  approvalStatusColor,
  apiError,
} from "../utils/gatewayUtils";
import GatewayStatCard from "../components/GatewayStatCard";
import GatewayTimeline from "../components/GatewayTimeline";
import GatewayFilterBar from "../components/GatewayFilterBar";
import { useToast } from "../../../../hooks/use-toast";
import { cn } from "../../../lib/utils";
import "../../ManagementDialog.css";
import "../../CloudAnalytics/CloudEntityAnalytics.css";
import "../AgenticGateway.css";



interface Agent {
  id: string;
  agentName?: string;
  agentVersion?: string;
  protocolVersion?: string;
  approvalStatus?: string;
  status?: string; // lifecycle: ACTIVE | DEPROVISIONED
  connectedSessions?: number;
  totalSessions?: number;
  totalRequests?: number;
  firstSeenAt?: string;
  lastSeenAt?: string;
  authClientId?: string;
  capabilities?: any;
  // Unified-agent enrichment (same AgentDto for list + detail; A2A agents also carry baseUrl/skills)
  protocols?: string[]; // subset of ["MCP", "A2A"]
  workloadId?: string;
  identitySource?: string; // "KEYCLOAK" | "SPIFFE" | undefined (self-asserted)
  verified?: boolean;
  baseUrl?: string; // A2A endpoint; undefined for MCP-only
  toolCount?: number;
  tools?: string[]; // provisioned MCP tools
  skillCount?: number;
  skills?: string[]; // skills this agent exposes (dot-named publicName)
  invokableSkills?: string[]; // skills it may invoke on others
}

interface AgentHealth {
  reachable?: boolean;
  cardName?: string;
}

interface AgentSummary {
  totalAgents?: number;
  activeAgents?: number;
  connectedSessions?: number;
  totalRequests?: number;
  pendingApproval?: number;
}

interface Session {
  sessionId: string;
  authMethod?: string;
  tokenType?: string;
  calledByName?: string;
  userIdentity?: string;
  humanUserName?: string;
  nhiServiceName?: string;
  nhiClientId?: string;
  connectedAt?: string;
  disconnectedAt?: string;
  requestCount?: number;
  lastRequestAt?: string;
  ipAddress?: string;
  status?: string;
}

interface LiveSession {
  sessionId: string;
  agentId?: string;
  agentName?: string;
  agentVersion?: string;
  approvalStatus?: string;
  authMethod?: string;
  connectedDurationMs?: number;
  idleDurationMs?: number;
  requestCount?: number;
  lastRequestAt?: string;
}

interface LiveSessionsData {
  connectedSessions?: LiveSession[];
  totalConnected?: number;
  totalAgentsOnline?: number;
}

interface AnalyticsData {
  totalRequests?: number;
  errorRate?: number;
  avgLatencyMs?: number;
  p95LatencyMs?: number;
  requestsByHour?: Array<{ hour: string; count: number }>;
  errorBreakdown?: Array<{ errorCode?: number; errorMessage?: string; count: number }>;
  toolUsage?: Array<{ toolName: string; serverName?: string; count: number; avgLatencyMs?: number }>;
  serverUsage?: Array<{ serverName: string; requestCount: number; errorCount: number }>;
}

interface AccessData {
  assignedProfiles?: Array<{ id: string; name: string; description?: string }>;
  serverBreakdown?: Record<
    string,
    { allowedTools?: number; totalTools?: number; allowedPrompts?: number; totalPrompts?: number; allowedResources?: number; totalResources?: number }
  >;
}

interface TimelineEvent {
  id?: string;
  timestamp: string;
  eventType?: string;
  module?: string;
  status: string;
  durationMs?: number;
  duration?: number;
  capabilityName?: string;
  serverName?: string;
  serverConfigName?: string;
  correlationId?: string;
  errorMessage?: string;
}

interface TimelineData {
  session?: Partial<Session> & { agentName?: string };
  timeline?: TimelineEvent[];
  totalPages?: number;
  totalElements?: number;
}


function formatDurationMs(ms: number | null | undefined): string {
  if (ms == null || ms < 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ${secs % 60}s`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${mins % 60}m`;
}

// Reachability = inbound: only A2A agents are endpoints others can reach (MCP agents are clients, not servers).
function reachableVia(agent: Agent): string[] {
  return agent.protocols?.includes("A2A") ? ["A2A"] : [];
}

// Calls = outbound: MCP if it has provisioned tools / speaks MCP; A2A if it may invoke skills on others.
function callsVia(agent: Agent): string[] {
  const out: string[] = [];
  if (agent.protocols?.includes("MCP") || (agent.toolCount ?? 0) > 0) out.push("MCP");
  if ((agent.invokableSkills?.length ?? 0) > 0) out.push("A2A");
  return out;
}

function ProtoChips({ protocols }: { protocols: string[] }) {
  if (!protocols.length) return <span className="text-muted-foreground/50">—</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {protocols.map((p) => (
        <GatewayProtocolBadge key={p} protocol={p} />
      ))}
    </span>
  );
}

// Connectivity row — label + caption in a fixed left column, chips/detail on the right (mirrors the OG layout).
function ConnRow({ label, caption, children }: { label: string; caption: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[150px_1fr] gap-4 items-start">
      <div>
        <div className="text-[13px] font-semibold text-foreground">{label}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{caption}</div>
      </div>
      <div className="flex items-center gap-2.5 flex-wrap min-h-[24px]">{children}</div>
    </div>
  );
}

function normalizeHourlyForChart(
  raw: Array<{ hour: string; count: number }> = []
): Array<{ label: string; count: number }> {
  const map = new Map<string, number>();
  raw.forEach((h) => {
    if (h.hour) {
      const d = new Date(h.hour);
      if (!isNaN(d.getTime())) {
        d.setMinutes(0, 0, 0);
        const key = d.toISOString().slice(0, 13);
        map.set(key, (map.get(key) || 0) + (h.count || 0));
      }
    }
  });

  const result: Array<{ label: string; count: number }> = [];
  const now = new Date();
  now.setMinutes(0, 0, 0);
  for (let i = 23; i >= 0; i--) {
    const d = new Date(now.getTime() - i * 3600000);
    const key = d.toISOString().slice(0, 13);
    result.push({
      label: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }),
      count: map.get(key) || 0,
    });
  }
  return result;
}

async function copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
  }
}


function LoadingSpinner({ text = "Loading…" }: { text?: string }) {
  return (
    <div className="ag-loading">
      <span className="ag-spinner animate-spin" aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

function EmptyState({ message, sub }: { message: string; sub?: string }) {
  return (
    <div className="ag-empty h-full">
      <span className="ag-empty-icon" aria-hidden="true">
        <Bot />
      </span>
      <p className="ag-empty-title">{message}</p>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function ApprovalBadge({ status }: { status?: string }) {
  return (
    <Badge
      className={cn(
        "text-[10px] border font-medium px-2 py-0",
        approvalStatusColor(status || "")
      )}
    >
      {status || "-"}
    </Badge>
  );
}

// Terminal lifecycle badge: DEPROVISIONED overrides the approval badge.
function StateBadge({ status, approvalStatus }: { status?: string; approvalStatus?: string }) {
  if (status === "DEPROVISIONED") {
    return (
      <Badge className="text-[10px] border font-medium px-2 py-0 bg-card text-muted-foreground border-border/70 shadow-[0_1px_3px_rgb(15_23_42/0.06)]">
        DEPROVISIONED
      </Badge>
    );
  }
  return <ApprovalBadge status={approvalStatus} />;
}

function ConnectionBadge({ connected }: { connected: boolean }) {
  return (
    <Badge
      className={cn(
        "flex items-center gap-1 text-xs border",
        connected
          ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
          : "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30"
      )}
    >
      <span
        className={cn(
          "w-1.5 h-1.5 rounded-full",
          connected ? "bg-emerald-400" : "bg-red-400"
        )}
      />
      {connected ? "Connected" : "Disconnected"}
    </Badge>
  );
}

function StatPill({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 ag-surface rounded-lg px-2.5 py-1.5 flex-1 min-w-0">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground leading-none mb-0.5">{label}</p>
        <p
          className={cn(
            "text-sm font-semibold leading-none",
            highlight ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}


function AgentCard({
  agent,
  onSelect,
  sensitivity,
}: {
  agent: Agent;
  onSelect: (id: string) => void;
  sensitivity?: string;
}) {
  const connected = agent.connectedSessions || 0;
  const isConnected = connected > 0;

  return (
    <div
      className="ag-entity ag-clickable"
      onClick={() => onSelect(agent.id)}
    >
      <div className="ag-entity-head">
        <div className="ag-entity-identity">
          <span className="ag-entity-avatar" aria-hidden="true">
            <Bot />
            <span className="ag-server-dot" data-tone={isConnected ? "success" : "danger"} />
          </span>
          <div className="min-w-0">
            <h3 className="ag-entity-name" title={agent.agentName || "Unnamed Agent"}>
              {agent.agentName || "Unnamed Agent"}
            </h3>
            <div className="ag-entity-sub">
              <span className="ag-status" data-tone={isConnected ? "success" : "danger"}>
                <span className={cn("ag-status-dot", isConnected && "animate-pulse")} aria-hidden="true" />
                {isConnected ? "Connected" : "Disconnected"}
              </span>
              {agent.agentVersion && <span className="ag-hint">v{agent.agentVersion}</span>}
            </div>
          </div>
        </div>
        <StateBadge status={agent.status} approvalStatus={agent.approvalStatus} />
      </div>

      <div className="ag-entity-body">
        <div className="ag-server-metrics ag-metrics-3">
          <div className="ag-metric">
            <span className="ag-metric-label">Sessions</span>
            <span className="ag-metric-value text-lg">{agent.totalSessions || 0}</span>
          </div>
          <div className="ag-metric">
            <span className="ag-metric-label">Requests</span>
            <span className="ag-metric-value text-lg">{agent.totalRequests || 0}</span>
          </div>
          <div className="ag-metric" data-tone={connected > 0 ? "success" : undefined}>
            <span className="ag-metric-label">Live</span>
            <span className="ag-metric-value text-lg">{connected}</span>
          </div>
        </div>

        <div className="ag-kv-list">
          <div className="ag-kv">
            <span>Reachable via</span>
            <ProtoChips protocols={reachableVia(agent)} />
          </div>
          <div className="ag-kv">
            <span>Calls via</span>
            <ProtoChips protocols={callsVia(agent)} />
          </div>
          <div className="ag-kv">
            <span>Capabilities</span>
            <span>
              {agent.toolCount ?? 0} tools · {agent.skillCount ?? 0} skills
            </span>
          </div>
          <div className="ag-kv">
            <span>First Seen</span>
            <span>{timeAgo(agent.firstSeenAt)}</span>
          </div>
          <div className="ag-kv">
            <span>Last Seen</span>
            <span>{timeAgo(agent.lastSeenAt)}</span>
          </div>
          {sensitivity && (
            <div className="ag-kv">
              <span>Sensitivity</span>
              <GatewaySensitivityBadge value={sensitivity} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function CapList({
  title,
  caption,
  items,
  badge,
  icon,
  sensitivityFor,
}: {
  title: string;
  caption: string;
  items?: string[];
  badge?: string;
  icon: React.ReactNode;
  sensitivityFor?: (name: string) => string;
}) {
  const list = items || [];
  return (
    <Card className="ag-card">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="text-base text-foreground/80 flex items-center gap-2">
          {icon}
          {title} ({list.length})
        </CardTitle>
        <p className="text-xs text-muted-foreground">{caption}</p>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {list.length === 0 ? (
          <p className="text-xs text-muted-foreground/60">None</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {list.map((name) => (
              <div key={name} className="flex items-center gap-2 rounded-lg border border-border ag-surface px-3 py-2 portal-card-hover">
                {badge && <GatewayCapabilityTypeBadge type={badge} />}
                <code className="text-xs text-primary break-all">{name}</code>
                {sensitivityFor && (
                  <span className="ml-auto shrink-0">
                    <GatewaySensitivityBadge value={sensitivityFor(name)} tag />
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewTab({
  agent,
  health,
  onRemoveA2a,
  sensSkills,
}: {
  agent: Agent;
  health: AgentHealth | null;
  onRemoveA2a: () => void;
  sensSkills: any[];
}) {
  const [expanded, setExpanded] = useState(false);
  const caps = agent.capabilities?.capabilities || {};
  const capKeys = Object.keys(caps);
  const isA2A = agent.protocols?.includes("A2A") === true;
  const reach = reachableVia(agent);
  const calls = callsVia(agent);

  return (
    <div className="space-y-6">
      <Card className="ag-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base text-foreground/80">Agent Metadata</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground mb-0.5">Protocol Version</p>
              <p className="text-foreground font-mono">{agent.protocolVersion || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Agent Version</p>
              <p className="text-foreground font-mono">{agent.agentVersion || "-"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Approval Status</p>
              <StateBadge status={agent.status} approvalStatus={agent.approvalStatus} />
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">First Seen</p>
              <p className="text-foreground">{formatTimestamp(agent.firstSeenAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Last Seen</p>
              <p className="text-foreground">{formatTimestamp(agent.lastSeenAt)}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Total Sessions</p>
              <p className="text-foreground">{agent.totalSessions || 0}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Total Requests</p>
              <p className="text-foreground">{agent.totalRequests || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connectivity — OG layout: label/caption column + chips/detail, with Endpoint on top */}
      <Card className="ag-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-base text-foreground/80">Connectivity</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 flex flex-col gap-4">
          <ConnRow label="Endpoint" caption="the agent's A2A base URL">
            {isA2A && agent.baseUrl ? (
              <span className="font-mono text-xs text-foreground/90 break-all">{agent.baseUrl}</span>
            ) : (
              <span className="text-xs text-muted-foreground/60">—</span>
            )}
          </ConnRow>
          <ConnRow label="Reachable via" caption="inbound — how others reach this agent">
            <ProtoChips protocols={reach} />
            {reach.length === 0 && (
              <span className="text-xs text-muted-foreground">not an endpoint — nothing invokes it</span>
            )}
          </ConnRow>
          <ConnRow label="Calls via" caption="outbound — what this agent calls">
            <ProtoChips protocols={calls} />
          </ConnRow>
        </CardContent>
      </Card>

      {/* Identity — verified workload / source / ids */}
      <Card className="ag-card">
        <CardHeader className="pb-2 pt-4 px-4 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base text-foreground/80">Identity</CardTitle>
          {isA2A && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRemoveA2a}
              className="h-7 border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
              title="Remove this agent's A2A registration"
            >
              <Link2Off className="w-3.5 h-3.5 mr-1.5" /> Remove A2A registration
            </Button>
          )}
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs">
            <div>
              <p className="text-muted-foreground mb-0.5">Verified</p>
              {agent.verified ? (
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <Shield className="w-3.5 h-3.5" /> Verified
                </span>
              ) : (
                <span className="text-muted-foreground">Unverified</span>
              )}
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Identity Source</p>
              <p className="text-foreground">{agent.identitySource || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Workload ID</p>
              <p className="text-foreground font-mono break-all">{agent.workloadId || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Auth Client ID</p>
              <p className="text-foreground font-mono break-all">{agent.authClientId || "—"}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-0.5">Version</p>
              <p className="text-foreground font-mono">{agent.agentVersion || "—"}</p>
            </div>
            {isA2A && (
              <div>
                <p className="text-muted-foreground mb-0.5">Base URL</p>
                <p className="text-foreground font-mono break-all">{agent.baseUrl || "—"}</p>
              </div>
            )}
            {isA2A && health && health.reachable != null && (
              <div>
                <p className="text-muted-foreground mb-0.5">Reachable</p>
                <p className={cn(health.reachable ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400")}>
                  {health.reachable ? "Yes" : "No"}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Capability lists — exposed skills / invokable / provisioned tools */}
      <CapList
        title="Exposed Skills"
        caption="capabilities this agent offers to others"
        items={agent.skills}
        badge="SKILL"
        icon={<GraduationCap className="w-4 h-4 text-primary" />}
        sensitivityFor={(name) => skillSensitivity(sensSkills, agent.id, name)}
      />
      <CapList
        title="Invokable Capabilities"
        caption="what this agent is allowed to call"
        items={agent.invokableSkills}
        icon={<Link2 className="w-4 h-4 text-primary" />}
      />
      <CapList
        title="Provisioned Tools"
        caption="MCP tools available to this agent"
        items={agent.tools}
        badge="TOOL"
        icon={<Wrench className="w-4 h-4 text-primary" />}
      />

      {capKeys.length > 0 && (
        <div>
          <h3 className="text-base font-medium text-foreground/80 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            Declared Capabilities
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {capKeys.map((key) => {
              const val = caps[key];
              const hasDetail =
                val && typeof val === "object" && Object.keys(val).length > 0;
              return (
                <Card
                  key={key}
                  className="ag-card"
                >
                  <CardContent className="px-3 py-3">
                    <p className="text-xs font-mono text-primary mb-1.5">{key}</p>
                    {hasDetail ? (
                      Object.entries(val).map(([k, v]) => (
                        <p key={k} className="text-xs text-muted-foreground">
                          {k}:{" "}
                          <span className="text-foreground">
                            {typeof v === "object" ? JSON.stringify(v) : String(v)}
                          </span>
                        </p>
                      ))
                    ) : (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">Supported</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {agent.capabilities && (
        <Card className="ag-card">
          <CardHeader
            className="pb-2 pt-4 px-4 cursor-pointer"
            onClick={() => setExpanded((p) => !p)}
          >
            <div className="flex items-center gap-2">
              {expanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
              <CardTitle className="text-sm text-muted-foreground font-normal">
                Raw Initialize Payload
              </CardTitle>
            </div>
          </CardHeader>
          {expanded && (
            <CardContent className="px-4 pb-4">
              <pre className="text-xs text-foreground/80 ag-surface rounded-lg p-3 overflow-x-auto font-mono leading-relaxed max-h-64">
                {JSON.stringify(agent.capabilities, null, 2)}
              </pre>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}


function CapabilityAccessTab({
  agentId,
  agentName,
}: {
  agentId: string;
  agentName: string;
}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [accessData, setAccessData] = useState<AccessData | null>(null);
  const [allProfiles, setAllProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfileId, setSelectedProfileId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [access, profiles] = await Promise.all([
        GatewayProfilesApi.agentAccess(agentId),
        GatewayProfilesApi.list().catch(() => []),
      ]);
      setAccessData(access);
      setAllProfiles(Array.isArray(profiles) ? profiles : []);
    } catch (e: any) {
      toast({
        title: "Failed to load access data",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingSpinner text="Loading capability access…" />;

  const profiles = accessData?.assignedProfiles || [];
  const serverBreakdown = accessData?.serverBreakdown || {};
  const assignedIds = new Set(profiles.map((p) => p.id));
  const unassigned = allProfiles.filter((p) => !assignedIds.has(p.id));

  const handleAssign = async () => {
    if (!selectedProfileId) {
      toast({ title: "Select a profile first", variant: "destructive" });
      return;
    }
    try {
      await GatewayProfilesApi.assign(selectedProfileId, agentId);
      toast({ title: "Profile assigned" });
      setSelectedProfileId("");
      load();
    } catch (e: any) {
      toast({ title: "Assignment failed", description: apiError(e), variant: "destructive" });
    }
  };

  const handleUnassign = async (profileId: string) => {
    try {
      await GatewayProfilesApi.unassign(profileId, agentId);
      toast({ title: "Profile removed" });
      load();
    } catch (e: any) {
      toast({ title: "Unassignment failed", description: apiError(e), variant: "destructive" });
    }
  };

  const serverNames = Object.keys(serverBreakdown);

  return (
    <div className="space-y-6">
      <Card className="ag-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-sm text-foreground/80">
              Assigned Profiles ({profiles.length})
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-7 border-border text-muted-foreground hover:text-foreground text-xs"
              onClick={() => navigate("/agentic-gateway/profiles")}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Create New Profile
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          {profiles.length === 0 ? (
            <div className="text-center py-6 space-y-1">
              <Shield className="w-8 h-8 text-muted-foreground/70 mx-auto" />
              <p className="text-sm text-muted-foreground">No profiles assigned (default deny)</p>
              <p className="text-xs text-muted-foreground">
                This agent cannot see any capabilities. Assign an existing profile below, or{" "}
                <button
                  onClick={() => navigate("/agentic-gateway/profiles")}
                  className="text-primary hover:underline"
                >
                  create a new one
                </button>
                .
              </p>
            </div>
          ) : (
            profiles.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between gap-3 rounded-lg ag-surface px-3 py-2.5"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{p.name}</p>
                  {p.description && (
                    <p className="text-xs text-muted-foreground">{p.description}</p>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 dark:text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7"
                  onClick={() => handleUnassign(p.id)}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  Remove
                </Button>
              </div>
            ))
          )}

          {unassigned.length > 0 && (
            <div className="flex gap-2 mt-3">
              <select
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
                className="flex-1 ag-surface border border-border rounded-md text-sm text-foreground/80 px-3 py-1.5 focus:outline-none focus:border-primary/50"
              >
                <option value="">Select profile to assign…</option>
                {unassigned.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_4px_8px_-2px_hsl(var(--primary)/0.25)] text-xs"
                onClick={handleAssign}
              >
                Assign
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {serverNames.length > 0 && (
        <Card className="ag-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Effective Access</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-xs text-muted-foreground">Server</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Tools</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Prompts</TableHead>
                  <TableHead className="text-xs text-muted-foreground text-center">Resources</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {serverNames.map((srv) => {
                  const bd = serverBreakdown[srv];
                  return (
                    <TableRow key={srv} className="border-border hover:bg-primary/[0.025]">
                      <TableCell className="text-xs text-foreground/80 flex items-center gap-2">
                        <Server className="w-3.5 h-3.5 text-primary shrink-0" />
                        {srv}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedTools ?? 0} / {bd.totalTools ?? 0}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedPrompts ?? 0} / {bd.totalPrompts ?? 0}
                      </TableCell>
                      <TableCell className="text-xs text-foreground/80 text-center">
                        {bd.allowedResources ?? 0} / {bd.totalResources ?? 0}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function AnalyticsTab({ agentId }: { agentId: string }) {
  const { toast } = useToast();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    GatewayAgentsApi.analytics(agentId, 24)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          toast({
            title: "Failed to load analytics",
            description: apiError(e),
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, toast]);

  if (loading) return <LoadingSpinner text="Loading analytics…" />;

  if (!data || (data.totalRequests || 0) === 0) {
    return (
      <EmptyState
        message="No analytics data available yet"
        sub="Data will appear after the agent makes some requests."
      />
    );
  }

  const hourlyData = normalizeHourlyForChart(data.requestsByHour || []);
  const lastHourCount =
    hourlyData.length ? hourlyData[hourlyData.length - 1].count : 0;
  const errorRate = Number(data.errorRate || 0);
  const avgLatency =
    data.avgLatencyMs != null ? Math.round(data.avgLatencyMs) : null;
  const p95Latency =
    data.p95LatencyMs != null ? Math.round(data.p95LatencyMs) : null;

  const errors = data.errorBreakdown || [];
  const tools = data.toolUsage || [];
  const servers = data.serverUsage || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <GatewayStatCard
          icon={<TrendingUp className="w-5 h-5" />}
          label="Total Requests"
          value={data.totalRequests || 0}
          subtitle={`+${lastHourCount} last hr`}
          colorClass="text-primary"
        />
        <GatewayStatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Error Rate"
          value={`${errorRate.toFixed(1)}%`}
          subtitle={errorRate > 0 ? "increasing" : "stable"}
          colorClass={errorRate > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}
        />
        <GatewayStatCard
          icon={<Clock className="w-5 h-5" />}
          label="Avg Latency"
          value={avgLatency != null ? `${avgLatency}ms` : "-"}
          subtitle={avgLatency != null && avgLatency < 1000 ? "stable" : "elevated"}
          colorClass={
            avgLatency != null && avgLatency < 1000
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-amber-600 dark:text-amber-400"
          }
        />
        <GatewayStatCard
          icon={<Zap className="w-5 h-5" />}
          label="P95 Latency"
          value={p95Latency != null ? `${p95Latency}ms` : "-"}
          subtitle={p95Latency != null && p95Latency < 1500 ? "improving" : "slower"}
          colorClass={
            p95Latency != null && p95Latency < 1500
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-red-600 dark:text-red-400"
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="ag-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-foreground/80">
                Requests by Hour (24h)
              </CardTitle>
              <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 border text-xs">
                Live Traffic
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">Distribution of incoming traffic volume</p>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart
                data={hourlyData}
                margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient id="agentAreaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: "#64748b" }}
                  tickLine={false}
                  axisLine={false}
                  interval={4}
                />
                <YAxis
                  tick={{ fontSize: 9, fill: "#64748b" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 10,
                    boxShadow: "0 8px 24px -6px rgb(15 23 42 / 0.18)",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                  itemStyle={{ color: "#0891b2" }}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#06b6d4"
                  strokeWidth={1.5}
                  fill="url(#agentAreaGrad)"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="ag-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Error Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {errors.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 gap-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-500/40" />
                <p className="text-xs text-muted-foreground">No errors captured in selected window</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Error Type</TableHead>
                      <TableHead className="text-xs text-muted-foreground text-right">Count</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {errors.map((e, idx) => (
                      <TableRow key={idx} className="border-border hover:bg-primary/[0.025]">
                        <TableCell className="py-2">
                          <p className="text-xs font-mono text-red-600 dark:text-red-400">
                            {e.errorCode != null ? `Error ${e.errorCode}` : "Unknown Error"}
                          </p>
                          {e.errorMessage && (
                            <p className="text-xs text-muted-foreground mt-0.5">{e.errorMessage}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-right py-2">
                          <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30 border text-xs">
                            {e.count}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="ag-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Tool Usage Statistics</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {tools.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No tool usage yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Tool</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Calls</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Avg Latency</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tools.map((t, idx) => (
                      <TableRow key={idx} className="border-border hover:bg-primary/[0.025]">
                        <TableCell className="py-2">
                          <p className="text-xs font-mono text-primary">{t.toolName || "-"}</p>
                          {t.serverName && (
                            <p className="text-xs text-muted-foreground mt-0.5">{t.serverName}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-foreground/80 py-2">
                          {t.count || 0} call{(t.count || 0) !== 1 ? "s" : ""}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground py-2">
                          {t.avgLatencyMs != null ? `${Math.round(t.avgLatencyMs)}ms` : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="ag-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm text-foreground/80">Server Usage</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {servers.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No server usage yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs text-muted-foreground">Server</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Requests</TableHead>
                      <TableHead className="text-xs text-muted-foreground">Errors</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {servers.map((s, idx) => {
                      const err = s.errorCount || 0;
                      return (
                        <TableRow key={idx} className="border-border hover:bg-primary/[0.025]">
                          <TableCell className="text-xs text-foreground/80 py-2">
                            {s.serverName || "-"}
                          </TableCell>
                          <TableCell className="text-xs text-foreground/80 py-2">
                            {s.requestCount || 0}
                          </TableCell>
                          <TableCell className="py-2">
                            {err > 0 ? (
                              <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30 border text-xs">
                                {err}
                              </Badge>
                            ) : (
                              <span className="text-xs text-emerald-600 dark:text-emerald-400">0</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


interface PagedSessions {
  content: any[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface AgentSessionFilterValues {
  status: string[];
  authMethod: string[];
  tokenType: string[];
  callerType: string[];
  ipAddress: string;
  fromDate: string;
  toDate: string;
  search: string;
}

const EMPTY_AGENT_SESSION_FILTERS: AgentSessionFilterValues = {
  status: [],
  authMethod: [],
  tokenType: [],
  callerType: [],
  ipAddress: "",
  fromDate: "",
  toDate: "",
  search: "",
};

function SessionsTab({
  agentId,
  currentAgent,
}: {
  agentId: string;
  currentAgent: Agent | null;
}) {
  const { toast } = useToast();
  const [paged, setPaged] = useState<PagedSessions>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    number: 0,
    size: 15,
  });
  const [loading, setLoading] = useState(true);
  const [killTarget, setKillTarget] = useState<KillTarget>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelinePage, setTimelinePage] = useState(0);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>([]);
  const [timelineTotalPages, setTimelineTotalPages] = useState(0);
  const [timelineTotalElements, setTimelineTotalElements] = useState(0);

  const [draftFilters, setDraftFilters] = useState<AgentSessionFilterValues>(EMPTY_AGENT_SESSION_FILTERS);
  const [filters, setFilters] = useState<AgentSessionFilterValues>(EMPTY_AGENT_SESSION_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(15);
  const [sort] = useState("connectedAt,desc");
  const [filterOptions, setFilterOptions] = useState<{
    statuses: string[];
    callerTypes: string[];
    authMethods: string[];
    tokenTypes: string[];
  }>({ statuses: [], callerTypes: [], authMethods: [], tokenTypes: [] });

  useEffect(() => {
    let cancelled = false;
    GatewayAgentsApi.sessionFilters(agentId)
      .then((data: any) => {
        if (cancelled) return;
        setFilterOptions({
          statuses: data?.statuses || [],
          callerTypes: data?.callerTypes || [],
          authMethods: data?.authMethods || [],
          tokenTypes: data?.tokenTypes || [],
        });
      })
      .catch(() => {
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => {
    setPage(0);
  }, [filters, size]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params: any = {
      page,
      size,
      sort,
    };
    if (filters.status.length) params.status = filters.status;
    if (filters.authMethod.length) params.authMethod = filters.authMethod;
    if (filters.tokenType.length) params.tokenType = filters.tokenType;
    if (filters.callerType.length) params.callerType = filters.callerType;
    if (filters.ipAddress) params.ipAddress = filters.ipAddress;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    if (filters.search) params.search = filters.search;

    GatewayAgentsApi.sessions(agentId, params)
      .then((data: any) => {
        if (cancelled) return;
        setPaged({
          content: Array.isArray(data?.content) ? data.content : [],
          totalElements: data?.totalElements || 0,
          totalPages: data?.totalPages || 0,
          number: typeof data?.number === "number" ? data.number : 0,
          size: typeof data?.size === "number" ? data.size : size,
        });
      })
      .catch((e: Error) => {
        if (!cancelled) {
          toast({
            title: "Failed to load sessions",
            description: apiError(e),
            variant: "destructive",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, page, size, sort, filters, refreshKey]);

  const handleFilterChange = (key: string, value: string | string[]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value } as AgentSessionFilterValues));
  };
  const handleApplyFilters = () => {
    setFilters(draftFilters);
  };
  const handleClearFilters = () => {
    setDraftFilters(EMPTY_AGENT_SESSION_FILTERS);
    setFilters(EMPTY_AGENT_SESSION_FILTERS);
  };

  const sessions = paged.content as Session[];
  const totalElements = paged.totalElements;
  const totalPages = paged.totalPages;
  const currentPage = paged.number;
  const pageSize = paged.size || size;
  const from = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const to = Math.min((currentPage + 1) * pageSize, totalElements);

  const loadTimeline = useCallback(
    async (sessionId: string, page: number, reset: boolean) => {
      setTimelineLoading(true);
      try {
        const data: TimelineData = await GatewayAgentsApi.sessionTimeline(
          sessionId,
          page,
          50
        );
        const events: TimelineEvent[] = (data.timeline || []).map((e) => ({
          ...e,
          id: e.id || `${e.timestamp}-${Math.random()}`,
          duration: e.durationMs,
          serverConfigName: e.serverName,
        }));
        if (reset) {
          setTimelineEvents(events);
        } else {
          setTimelineEvents((prev) => [...prev, ...events]);
        }
        setTimelineTotalPages(data.totalPages || 0);
        setTimelineTotalElements(data.totalElements || 0);
        setTimelinePage(page);
      } catch (e: any) {
        toast({
          title: "Failed to load timeline",
          description: apiError(e),
          variant: "destructive",
        });
      } finally {
        setTimelineLoading(false);
      }
    },
    [toast]
  );

  const handleToggleTimeline = async (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      setTimelineEvents([]);
      return;
    }
    setExpandedSessionId(sessionId);
    setTimelineEvents([]);
    await loadTimeline(sessionId, 0, true);
  };

  const handleLoadMore = async () => {
    if (!expandedSessionId) return;
    await loadTimeline(expandedSessionId, timelinePage + 1, false);
  };

  return (
    <div className="space-y-4">
      <GatewayFilterBar
        fields={[
          { key: "status", label: "Status", type: "multiselect", options: filterOptions.statuses },
          { key: "callerType", label: "Caller Type", type: "multiselect", options: filterOptions.callerTypes },
          { key: "authMethod", label: "Auth Method", type: "multiselect", options: filterOptions.authMethods },
          { key: "tokenType", label: "Token Type", type: "multiselect", options: filterOptions.tokenTypes },
          { key: "ipAddress", label: "IP Address", type: "ip", placeholder: "e.g. 192.168.1.* or 10.0.0.5" },
          { key: "fromDate", label: "From", type: "date" },
          { key: "toDate", label: "To", type: "date" },
          { key: "search", label: "Search", type: "text", placeholder: "Search sessions…" },
        ]}
        values={draftFilters as unknown as Record<string, string | string[]>}
        onChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />

      <p className="text-sm text-muted-foreground">
        Session History ({totalElements.toLocaleString()}){" "}
        <span className="text-muted-foreground text-xs">— click a row to view timeline</span>
      </p>

      {loading ? (
        <LoadingSpinner text="Loading sessions…" />
      ) : sessions.length === 0 ? (
        <EmptyState
          message="No sessions match the current filters"
          sub="Try clearing filters or adjusting the date range."
        />
      ) : (
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground min-w-[200px]">Session ID</TableHead>
              <TableHead className="text-xs text-muted-foreground">Auth</TableHead>
              <TableHead className="text-xs text-muted-foreground">Called By</TableHead>
              <TableHead className="text-xs text-muted-foreground">Caller Type</TableHead>
              <TableHead className="text-xs text-muted-foreground">Connected</TableHead>
              <TableHead className="text-xs text-muted-foreground">Disconnected</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Requests</TableHead>
              <TableHead className="text-xs text-muted-foreground">Last Request</TableHead>
              <TableHead className="text-xs text-muted-foreground">IP Address</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => {
              const isExpanded = expandedSessionId === s.sessionId;
              const { label: callerLabel, color: callerColor } = callerTypeLabel(
                s.tokenType
              );

              let calledByText = "-";
              if (s.tokenType === "HUMAN_DELEGATED") {
                calledByText =
                  s.calledByName || s.userIdentity || s.humanUserName || "-";
              } else if (s.tokenType === "AUTOMATED_AGENT") {
                calledByText = s.calledByName || s.nhiServiceName || "-";
              }

              return (
                <React.Fragment key={s.sessionId}>
                  <TableRow
                    className={cn(
                      "border-border cursor-pointer transition-colors",
                      isExpanded
                        ? "bg-primary/[0.04] hover:bg-primary/[0.05]"
                        : "hover:bg-primary/[0.025]"
                    )}
                    onClick={() => handleToggleTimeline(s.sessionId)}
                  >
                    <TableCell className="py-2">
                      <div className="flex items-center gap-1.5">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-primary shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        )}
                        <code className="text-xs text-foreground/80 font-mono truncate max-w-[160px]">
                          {s.sessionId || "-"}
                        </code>
                        <button
                          className="shrink-0 text-muted-foreground hover:text-foreground/80 p-0.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(s.sessionId || "");
                            toast({ title: "Session ID copied" });
                          }}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 py-2">
                      {s.authMethod || "-"}
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 py-2">
                      {calledByText}
                    </TableCell>
                    <TableCell className="py-2">
                      <Badge
                        className={cn("text-xs border py-0", callerColor)}
                      >
                        {callerLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      {formatTimestamp(s.connectedAt)}
                    </TableCell>
                    <TableCell className="text-xs py-2">
                      {s.disconnectedAt ? (
                        <span className="text-muted-foreground">
                          {formatTimestamp(s.disconnectedAt)}
                        </span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">active</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-foreground/80 text-center py-2">
                      {s.requestCount || 0}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2">
                      {formatTimestamp(s.lastRequestAt)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono py-2">
                      {s.ipAddress || "-"}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex items-center gap-2">
                        <Badge
                          className={cn(
                            "text-xs border",
                            s.status === "CONNECTED"
                              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                              : "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30"
                          )}
                        >
                          {s.status || "-"}
                        </Badge>
                        {s.status === "CONNECTED" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setKillTarget({
                                sessionId: s.sessionId,
                                agentId: currentAgent?.id,
                                agentName: currentAgent?.agentName,
                              });
                            }}
                            className="h-6 px-2 border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-[10px]"
                          >
                            <Ban className="w-3 h-3 mr-1" /> Kill
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>

                  {isExpanded && (
                    <TableRow className="border-border hover:bg-transparent">
                      <TableCell colSpan={10} className="py-0">
                        <div className="py-4 px-6 ag-surface border-t border-border">
                          {timelineLoading && timelineEvents.length === 0 ? (
                            <LoadingSpinner text="Loading timeline…" />
                          ) : timelineEvents.length === 0 ? (
                            <p className="text-xs text-muted-foreground text-center py-4">
                              No audit events for this session.
                            </p>
                          ) : (
                            <>
                              <GatewayTimeline events={timelineEvents} />
                              {timelinePage + 1 < timelineTotalPages && (
                                <div className="text-center mt-4">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-border text-muted-foreground text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleLoadMore();
                                    }}
                                    disabled={timelineLoading}
                                  >
                                    {timelineLoading ? (
                                      <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin mr-1" />
                                    ) : null}
                                    Load More (
                                    {timelineTotalElements -
                                      (timelinePage + 1) * 50}{" "}
                                    remaining)
                                  </Button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}

      {!loading && totalElements > 0 && (
        <div className="flex items-center justify-between px-4 py-3 rounded-lg border border-border bg-card">
          <span className="text-xs text-muted-foreground">
            Showing {from}–{to} of {totalElements.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-xs text-muted-foreground">
              Page {currentPage + 1} / {Math.max(1, totalPages)}
            </span>
            <button
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              className="p-1 rounded-md border border-border text-muted-foreground hover:text-foreground/80 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <select
              value={pageSize}
              onChange={(e) => setSize(Number(e.target.value))}
              className="ml-2 h-7 bg-card border border-border rounded-md px-2 text-xs text-foreground/80 outline-none"
            >
              {[10, 15, 25, 50].map((s) => (
                <option key={s} value={s}>
                  {s} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <KillSessionDialog target={killTarget} onClose={() => setKillTarget(null)} onKilled={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}


type KillTarget = { sessionId: string; agentId?: string; agentName?: string } | null;

// Shared kill-session dialog (reason + optional "also block agent").
function KillSessionDialog({
  target,
  onClose,
  onKilled,
}: {
  target: KillTarget;
  onClose: () => void;
  onKilled: () => void;
}) {
  const { toast } = useToast();
  const [reason, setReason] = useState("");
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target) {
      setReason("");
      setAlsoBlock(false);
    }
  }, [target]);

  const handleKill = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await GatewayStsApi.revokeSession({ sessionId: target.sessionId, reason: reason.trim() || undefined });
      if (alsoBlock && target.agentId) {
        await GatewayAgentsApi.block(target.agentId);
      }
      toast({ title: alsoBlock && target.agentId ? "Session killed · agent blocked" : "Session killed" });
      onKilled();
      onClose();
    } catch (e: any) {
      toast({ title: "Kill failed", description: apiError(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-sm">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
              <Ban />
            </span>
            <div>
              <DialogTitle>Kill Session</DialogTitle>
              <DialogDescription>Terminate this live session. The agent can reconnect with a new session unless you also block it.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          <div className="flex flex-col gap-3 py-1">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">Reason (optional)</label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. suspicious tool calls"
                className="ag-surface border-border"
              />
            </div>
            {target?.agentId && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={alsoBlock}
                  onChange={(e) => setAlsoBlock(e.target.checked)}
                  className="accent-primary w-4 h-4"
                />
                Also block this agent{target.agentName ? ` (${target.agentName})` : ""}
              </label>
            )}
          </div>
        </div>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleKill} disabled={busy} className="ag-danger-button">
            {busy && <div className="w-3.5 h-3.5 mr-2 border border-white border-t-transparent rounded-full animate-spin" />}
            Kill Session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Drill from a live session into its full audit timeline (works for stateless too).
type TimelineTarget = { sessionId: string; agentName?: string } | null;

function SessionTimelineDialog({
  target,
  onClose,
}: {
  target: TimelineTarget;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const loadPage = useCallback(
    async (sessionId: string, pageNum: number, reset: boolean) => {
      setLoading(true);
      try {
        const data: TimelineData = await GatewayAgentsApi.sessionTimeline(sessionId, pageNum, 50);
        const list: TimelineEvent[] = (data.timeline || []).map((e) => ({
          ...e,
          id: e.id || `${e.timestamp}-${Math.random()}`,
          duration: e.durationMs,
          serverConfigName: e.serverName,
        }));
        setEvents((prev) => (reset ? list : [...prev, ...list]));
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
        setPage(pageNum);
      } catch (e: any) {
        toast({ title: "Failed to load timeline", description: apiError(e), variant: "destructive" });
      } finally {
        setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (target) {
      setEvents([]);
      setPage(0);
      setTotalPages(0);
      setTotalElements(0);
      loadPage(target.sessionId, 0, true);
    }
  }, [target, loadPage]);

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-3xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <ListTree />
            </span>
            <div>
              <DialogTitle>Session Timeline</DialogTitle>
              <DialogDescription>
                {target?.agentName ? <span className="text-foreground/80">{target.agentName}</span> : null}
                {target?.agentName ? " · " : ""}
                <code className="text-primary font-mono text-xs">{target?.sessionId}</code>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          {loading && events.length === 0 ? (
            <LoadingSpinner text="Loading timeline…" />
          ) : events.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">No audit events for this session.</p>
          ) : (
            <>
              <GatewayTimeline events={events} />
              {page + 1 < totalPages && (
                <div className="text-center mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-border text-muted-foreground text-xs"
                    onClick={() => target && loadPage(target.sessionId, page + 1, false)}
                    disabled={loading}
                  >
                    {loading && (
                      <div className="w-3 h-3 border border-muted-foreground border-t-transparent rounded-full animate-spin mr-1" />
                    )}
                    Load More ({totalElements - (page + 1) * 50} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Full request-lifecycle chain across servers for a single trace id.
function TraceChainDialog({
  traceId,
  onClose,
}: {
  traceId: string | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (!traceId) return;
    let cancelled = false;
    setLoading(true);
    setData(null);
    GatewayAuditApi.trace(traceId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: any) => {
        if (!cancelled)
          toast({ title: "Failed to load trace", description: apiError(e), variant: "destructive" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [traceId, toast]);

  const events = Array.isArray(data) ? data : data?.events || data?.timeline || [];

  return (
    <Dialog open={!!traceId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-3xl">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <GitBranch />
            </span>
            <div>
              <DialogTitle>Trace Chain</DialogTitle>
              <DialogDescription>Full request lifecycle for trace <code className="text-primary font-mono text-xs">{traceId}</code></DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          {loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading trace…</div>
          ) : events.length > 0 ? (
            <GatewayTimeline events={events} />
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">No trace events</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function LiveSessionsTab() {
  const { toast } = useToast();
  const [data, setData] = useState<LiveSessionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [killTarget, setKillTarget] = useState<KillTarget>(null);
  const [timelineTarget, setTimelineTarget] = useState<TimelineTarget>(null);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await GatewayAgentsApi.liveSessions();
      setData(result);
    } catch (e: any) {
      toast({
        title: "Failed to load live sessions",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
    refreshRef.current = setInterval(load, 5000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [load]);

  if (loading) return <LoadingSpinner text="Loading live sessions…" />;

  const sessions = data?.connectedSessions || [];

  if (sessions.length === 0) {
    return (
      <EmptyState
        message="No active sessions right now"
        sub="Active sessions will appear here and refresh every 5 seconds."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg ag-surface border border-border px-4 py-2 text-center">
          <p className="text-lg font-bold text-foreground">{data?.totalConnected ?? 0}</p>
          <p className="text-xs text-muted-foreground">Connected</p>
        </div>
        <div className="rounded-lg ag-surface border border-border px-4 py-2 text-center">
          <p className="text-lg font-bold text-foreground">
            {data?.totalAgentsOnline ?? 0}
          </p>
          <p className="text-xs text-muted-foreground">Agents Online</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          Auto-refreshing every 5s
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Agent</TableHead>
              <TableHead className="text-xs text-muted-foreground min-w-[180px]">Session ID</TableHead>
              <TableHead className="text-xs text-muted-foreground">Auth</TableHead>
              <TableHead className="text-xs text-muted-foreground">Connected For</TableHead>
              <TableHead className="text-xs text-muted-foreground text-center">Requests</TableHead>
              <TableHead className="text-xs text-muted-foreground">Last Request</TableHead>
              <TableHead className="text-xs text-muted-foreground">Idle</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => {
              const idle = s.idleDurationMs || 0;
              const isActive = idle < 60000;
              return (
                <TableRow key={s.sessionId} className="border-border hover:bg-primary/[0.025]">
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "w-2 h-2 rounded-full",
                          isActive ? "bg-emerald-400 animate-pulse" : "bg-muted-foreground/50"
                        )}
                      />
                      <span className="text-xs text-foreground/80">
                        {s.agentName || "-"}
                      </span>
                      {s.agentVersion && (
                        <span className="text-xs text-muted-foreground font-mono">
                          v{s.agentVersion}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs text-muted-foreground font-mono">
                        {(s.sessionId || "").substring(0, 24)}…
                      </code>
                      <button
                        className="text-muted-foreground hover:text-foreground/80 p-0.5"
                        onClick={() => {
                          copyToClipboard(s.sessionId || "");
                          toast({ title: "Session ID copied" });
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 py-2">
                    {s.authMethod || "-"}
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 py-2">
                    {formatDurationMs(s.connectedDurationMs)}
                  </TableCell>
                  <TableCell className="text-xs text-foreground/80 text-center py-2">
                    {s.requestCount || 0}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground py-2">
                    {s.lastRequestAt ? formatTimestamp(s.lastRequestAt) : "-"}
                  </TableCell>
                  <TableCell className="text-xs py-2">
                    <span
                      className={cn(
                        idle < 60000 ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                      )}
                    >
                      {formatDurationMs(idle)}
                    </span>
                  </TableCell>
                  <TableCell className="py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setTimelineTarget({ sessionId: s.sessionId, agentName: s.agentName })
                        }
                        className="h-7 border-border text-muted-foreground hover:text-foreground text-xs"
                      >
                        <ListTree className="w-3.5 h-3.5 mr-1" /> Timeline
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setKillTarget({ sessionId: s.sessionId, agentId: s.agentId, agentName: s.agentName })
                        }
                        className="h-7 border-red-800/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-xs"
                      >
                        <Ban className="w-3.5 h-3.5 mr-1" /> Kill
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <KillSessionDialog target={killTarget} onClose={() => setKillTarget(null)} onKilled={load} />
      <SessionTimelineDialog target={timelineTarget} onClose={() => setTimelineTarget(null)} />
    </div>
  );
}


// Per-request activity rollup (grouped by trace) — works for stateless agents too.
// One edge group (Calls made / Calls received), rows grouped by protocol (A2A first, then MCP).
function EdgeTable({ title, caption, edges, peerLabel }: { title: string; caption: string; edges: any[]; peerLabel: string }) {
  const rank = (p: string) => (p === "A2A" ? 0 : p === "MCP" ? 1 : 2);
  const groups: Record<string, any[]> = {};
  edges.forEach((e) => {
    const p = e.protocol || "—";
    (groups[p] = groups[p] || []).push(e);
  });
  const protos = Object.keys(groups).sort((a, b) => rank(a) - rank(b));
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-card">
        <p className="text-sm font-medium text-foreground/80">{title}</p>
        <p className="text-[11px] text-muted-foreground">{caption}</p>
      </div>
      {edges.length === 0 ? (
        <p className="px-4 py-6 text-center text-xs text-muted-foreground/60">None</p>
      ) : (
        protos.map((p) => (
          <div key={p}>
            <div className="flex items-center gap-2 px-4 py-2 ag-surface border-b border-border">
              <GatewayProtocolBadge protocol={p} />
              <span className="text-[10px] text-muted-foreground">{groups[p].length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2 text-left font-medium">{peerLabel}</th>
                    <th className="px-3 py-2 text-left font-medium">Capability</th>
                    <th className="px-3 py-2 text-center font-medium">Calls</th>
                    <th className="px-3 py-2 text-center font-medium">Allow</th>
                    <th className="px-3 py-2 text-center font-medium">Deny</th>
                    <th className="px-3 py-2 text-left font-medium">Last</th>
                  </tr>
                </thead>
                <tbody>
                  {groups[p].map((e, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-primary/[0.025]">
                      <td className="px-4 py-2 text-foreground/90 font-medium">{e.peer || "—"}</td>
                      <td className="px-3 py-2 font-mono text-muted-foreground max-w-[160px] truncate" title={e.capability}>{e.capability || "—"}</td>
                      <td className="px-3 py-2 text-center text-foreground/80">{e.calls ?? 0}</td>
                      <td className="px-3 py-2 text-center text-emerald-600 dark:text-emerald-400">{e.allowed ?? 0}</td>
                      <td className={cn("px-3 py-2 text-center", (e.denied ?? 0) > 0 ? "text-red-600 dark:text-red-400" : "text-muted-foreground")}>{e.denied ?? 0}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{e.lastAt ? formatTimestamp(e.lastAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ActivityTab({ agent }: { agent: Agent }) {
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any | null>(null);
  const [humanNames, setHumanNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [traceTarget, setTraceTarget] = useState<string | null>(null);
  const agentKey = agent.authClientId || agent.agentName || "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [acts, sum, humans] = await Promise.all([
        GatewayAuditApi.activities(agentKey, 0, 50).catch(() => []),
        GatewayAuditApi.activitySummary(agentKey).catch(() => null),
        GatewayHumanUsersApi.list().catch(() => []),
      ]);
      if (cancelled) return;
      setRows(Array.isArray(acts) ? acts : (acts?.content || []));
      setSummary(sum);
      const map: Record<string, string> = {};
      const hlist = Array.isArray(humans) ? humans : (humans?.content || []);
      hlist.forEach((h: any) => {
        const id = h.id || h.humanUserId || h.userId;
        const name = h.preferredUsername || h.fullName || h.email || h.username;
        if (id && name) map[id] = name;
      });
      setHumanNames(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [agentKey]);

  if (loading) return <LoadingSpinner text="Loading activity…" />;

  const totals = summary?.totals;
  const outbound: any[] = summary?.outbound || [];
  const inbound: any[] = summary?.inbound || [];
  const hasSummary = !!summary && (outbound.length > 0 || inbound.length > 0);

  const calledBy = (r: any): { name: string; icon: React.ReactNode } => {
    if (r.tokenType === "HUMAN_DELEGATED")
      return { name: r.userIdentity || humanNames[r.humanUserId] || "Human user", icon: <User className="w-3 h-3" /> };
    if (r.tokenType === "AUTOMATED_AGENT")
      return { name: r.agentName || "Service account", icon: <Bot className="w-3 h-3" /> };
    return { name: r.userIdentity || humanNames[r.humanUserId] || "—", icon: null };
  };

  const capChunk = (arr: string[], n = 6) =>
    arr.length > n ? [...arr.slice(0, n), `(+${arr.length - n})`] : arr;

  const capCell = (r: any) => {
    const skills: string[] = Array.isArray(r.skills) ? r.skills : [];
    const mcp: string[] = Array.isArray(r.mcpTools) ? r.mcpTools : [];
    if (!skills.length && !mcp.length) return <span className="text-muted-foreground/60">—</span>;
    return (
      <div className="space-y-1">
        {skills.length > 0 && (
          <div className="flex items-start gap-1.5">
            <GatewayCapabilityTypeBadge type="SKILL" className="mt-px shrink-0" />
            <span className="text-foreground/80 break-words">{capChunk(skills).join(", ")}</span>
          </div>
        )}
        {mcp.length > 0 && (
          <div className="flex items-start gap-1.5">
            <GatewayCapabilityTypeBadge type="TOOL" className="mt-px shrink-0" />
            <span className="text-foreground/80 break-words">{capChunk(mcp).join(", ")}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {hasSummary && totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <GatewayStatCard icon={<Activity className="w-5 h-5" />} label="Calls Made" value={totals.calls ?? 0} colorClass="text-primary" />
          <GatewayStatCard icon={<CheckCircle2 className="w-5 h-5" />} label="Allowed" value={totals.allowed ?? 0} colorClass="text-emerald-600 dark:text-emerald-400" />
          <GatewayStatCard icon={<XCircle className="w-5 h-5" />} label="Denied" value={totals.denied ?? 0} colorClass={(totals.denied ?? 0) > 0 ? "text-red-600 dark:text-red-400" : "text-foreground"} />
          <GatewayStatCard icon={<Users className="w-5 h-5" />} label="Peers" value={totals.peers ?? 0} colorClass="text-primary" />
        </div>
      )}

      {hasSummary && (
        <div className="grid gap-4 lg:grid-cols-2">
          <EdgeTable title="Calls made" caption="what this agent invoked" edges={outbound} peerLabel="Peer" />
          <EdgeTable title="Calls received" caption="who invoked this agent" edges={inbound} peerLabel="Caller" />
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState message="No activity" sub="No per-request activity recorded for this agent." />
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-card">
                  {["Time", "Capability", "Outcome", "Called By", "Transport", "Server", "Trace"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const cb = calledBy(r);
                  return (
                    <tr key={r.traceId || i} className="border-b border-border hover:bg-primary/[0.025]">
                      <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap align-top">
                        {r.endedAt ? formatTimestamp(r.endedAt) : "—"}
                      </td>
                      <td className="px-3 py-2.5 max-w-[240px] align-top">{capCell(r)}</td>
                      <td className="px-3 py-2.5 align-top"><GatewayStatusBadge status={r.outcome || "—"} /></td>
                      <td className="px-3 py-2.5 text-muted-foreground max-w-[160px] truncate align-top">
                        <span className="inline-flex items-center gap-1.5">{cb.icon}{cb.name}</span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground align-top">{r.transport || "—"}</td>
                      <td className="px-3 py-2.5 text-muted-foreground align-top">{r.serverName || "—"}</td>
                      <td className="px-3 py-2.5 align-top">
                        {r.traceId ? (
                          <button
                            onClick={() => setTraceTarget(r.traceId)}
                            title={`View trace chain · ${r.traceId}`}
                            className="inline-flex items-center gap-1 font-mono text-[10px] text-primary hover:text-primary/80 hover:underline"
                          >
                            <GitBranch className="w-3 h-3" />
                            {r.traceId.slice(0, 8)}
                          </button>
                        ) : (
                          <span className="font-mono text-[10px] text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <TraceChainDialog traceId={traceTarget} onClose={() => setTraceTarget(null)} />
    </div>
  );
}


// A clickable item in the header "Actions" popover: colored title on top, small detail beneath.
function ActionMenuItem({
  icon,
  color,
  title,
  desc,
  onClick,
  disabled,
  busy,
}: {
  icon: React.ReactNode;
  color: string;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-primary/[0.025] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <span className={cn("flex items-center gap-2 text-sm font-medium", color)}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
        {title}
      </span>
      <span className="block text-xs text-muted-foreground leading-relaxed mt-0.5 pl-6">{desc}</span>
    </button>
  );
}


function AgentDetailView({
  agentId,
  onBack,
}: {
  agentId: string;
  onBack: () => void;
}) {
  const { toast } = useToast();
  const [agent, setAgent] = useState<Agent | null>(null);
  const [liveConnected, setLiveConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [deprovisioning, setDeprovisioning] = useState(false);
  const [confirmDeprovision, setConfirmDeprovision] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [health, setHealth] = useState<AgentHealth | null>(null);
  const [confirmRemoveA2a, setConfirmRemoveA2a] = useState(false);
  const [removingA2a, setRemovingA2a] = useState(false);
  const [sensSkills, setSensSkills] = useState<any[]>([]);

  const loadAgent = useCallback(async () => {
    setLoading(true);
    try {
      const [data, sens] = await Promise.all([
        GatewayAgentsApi.get(agentId),
        GatewayPostProcessorApi.entitySensitivity().catch(() => ({})),
      ]);
      setAgent(data);
      setSensSkills(Array.isArray(sens?.skills) ? sens.skills : []);
      // A2A endpoints are name-keyed; probe reachability for agents that speak A2A (degrade silently).
      if (data?.protocols?.includes("A2A") && data.agentName) {
        try {
          setHealth(await GatewayA2AApi.health(data.agentName));
        } catch {
          setHealth(null);
        }
      } else {
        setHealth(null);
      }
      // GET /{id} doesn't return connectedSessions — derive live-connected from live sessions
      try {
        const live = await GatewayAgentsApi.liveSessions();
        const sessions = live?.connectedSessions || [];
        setLiveConnected(
          sessions.some((s: any) => s.agentId === agentId || (data?.agentName && s.agentName === data.agentName))
        );
      } catch {
        setLiveConnected(false);
      }
    } catch (e: any) {
      toast({
        title: "Failed to load agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [agentId, toast]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const handleApprove = async () => {
    if (!agent) return;
    setApproving(true);
    try {
      const res = await GatewayAgentsApi.approve(agentId);
      toast({ title: res.message || "Agent approved" });
      loadAgent();
    } catch (e: any) {
      toast({
        title: "Failed to approve agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setApproving(false);
    }
  };

  const handleBlock = async () => {
    if (!agent) return;
    setBlocking(true);
    try {
      const res = await GatewayAgentsApi.block(agentId);
      const parts = [res.message || "Agent blocked"];
      if (res.sessionsTerminated > 0)
        parts.push(`${res.sessionsTerminated} session(s) terminated`);
      if (res.affectedHumanUsers > 0)
        parts.push(`${res.affectedHumanUsers} human(s) affected`);
      if (res.affectedNhis > 0)
        parts.push(`${res.affectedNhis} NHI(s) affected`);
      toast({ title: parts.join(" · ") });
      loadAgent();
    } catch (e: any) {
      toast({
        title: "Failed to block agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setBlocking(false);
    }
  };

  const handleRemoveA2a = async () => {
    if (!agent?.agentName) return;
    setRemovingA2a(true);
    try {
      await GatewayA2AApi.remove(agent.agentName);
      toast({ title: `A2A registration for '${agent.agentName}' removed` });
      setConfirmRemoveA2a(false);
      onBack();
    } catch (e: any) {
      toast({ title: "Failed to remove A2A registration", description: apiError(e), variant: "destructive" });
    } finally {
      setRemovingA2a(false);
    }
  };

  const handleDeprovision = async () => {
    if (!agent) return;
    setDeprovisioning(true);
    try {
      const res = await GatewayAgentsApi.deprovision(agentId);
      const parts = [res.message || "Agent deprovisioned"];
      if (res.sessionsTerminated > 0)
        parts.push(`${res.sessionsTerminated} session(s) terminated`);
      toast({ title: parts.join(" · ") });
      setConfirmDeprovision(false);
      loadAgent();
    } catch (e: any) {
      toast({
        title: "Failed to deprovision agent",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDeprovisioning(false);
    }
  };

  if (loading) {
    return (
      <div className="ag-card ag-content">
        <LoadingSpinner text="Loading agent details…" />
      </div>
    );
  }
  if (!agent) {
    return (
      <div className="ag-card ag-content">
        <div className="ag-empty h-full">
          <span className="ag-empty-icon text-amber-500" aria-hidden="true">
            <AlertTriangle />
          </span>
          <p className="ag-empty-title">Agent not found</p>
          <Button variant="outline" className="ag-button no-default-hover-elevate mt-2" onClick={onBack}>
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  const connected = liveConnected || (agent.connectedSessions || 0) > 0;
  const approvalStatus = agent.approvalStatus || "APPROVED";
  const isDeprovisioned = agent.status === "DEPROVISIONED";

  return (
    <>
      <div className="ag-card ag-detail-header">
        <div className="ag-detail-identity">
          <button
            type="button"
            onClick={onBack}
            className="ag-back-button"
            title="Back to Agents"
            aria-label="Back to Agents"
          >
            <ArrowLeft />
          </button>
          <span className="ag-detail-avatar relative" aria-hidden="true">
            <Bot />
            <span className="ag-server-dot" data-tone={connected ? "success" : "danger"} />
          </span>
          <div className="min-w-0">
            <h1 className="ag-detail-title">
              <span className="truncate">{agent.agentName || "Unnamed Agent"}</span>
            </h1>
            <div className="ag-detail-meta">
              <span className="ag-status" data-tone={connected ? "success" : "danger"}>
                <span className="ag-status-dot" aria-hidden="true" />
                {connected ? "Connected" : "Disconnected"}
              </span>
              {agent.agentVersion && <span>v{agent.agentVersion}</span>}
              <StateBadge status={agent.status} approvalStatus={agent.approvalStatus} />
            </div>
          </div>
        </div>

        {!isDeprovisioned && (
          <Popover open={actionsOpen} onOpenChange={setActionsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="ag-button no-default-hover-elevate shrink-0"
              >
                <Shield />
                Actions
                <ChevronDown />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={8} className="w-80 p-1.5 bg-card border-border rounded-xl shadow-[0_12px_32px_-8px_rgb(15_23_42/0.18)]">
              {(approvalStatus === "PENDING" || approvalStatus === "BLOCKED") && (
                <ActionMenuItem
                  icon={<CheckCircle2 className="w-4 h-4" />}
                  color="text-emerald-600 dark:text-emerald-400"
                  title="Approve"
                  desc="Allow this agent to connect and obtain tokens through the gateway."
                  disabled={approving}
                  busy={approving}
                  onClick={() => { setActionsOpen(false); handleApprove(); }}
                />
              )}
              {(approvalStatus === "PENDING" || approvalStatus === "APPROVED") && (
                <ActionMenuItem
                  icon={<Ban className="w-4 h-4" />}
                  color="text-red-600 dark:text-red-400"
                  title="Block"
                  desc="Immediately revoke access and terminate active sessions. Can be re-approved later."
                  disabled={blocking}
                  busy={blocking}
                  onClick={() => { setActionsOpen(false); handleBlock(); }}
                />
              )}
              <ActionMenuItem
                icon={<Trash2 className="w-4 h-4" />}
                color="text-foreground"
                title="Deprovision"
                desc="Terminal — kills all sessions and permanently prevents this agent from reconnecting."
                onClick={() => { setActionsOpen(false); setConfirmDeprovision(true); }}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="ag-tabs-root">
        <div className="ag-card ag-toolbar ag-nav">
          <TabsList unstyled aria-label="Agent details" className="cea-tabs ag-tabs">
            <TabsTrigger unstyled value="overview" className="cea-tab">
              <Bot className="shrink-0" />
              Overview
            </TabsTrigger>
            <TabsTrigger unstyled value="access" className="cea-tab">
              <Shield className="shrink-0" />
              Capability Access
            </TabsTrigger>
            <TabsTrigger unstyled value="analytics" className="cea-tab">
              <BarChart2 className="shrink-0" />
              Analytics
            </TabsTrigger>
            <TabsTrigger unstyled value="sessions" className="cea-tab">
              <Users className="shrink-0" />
              Sessions
            </TabsTrigger>
            <TabsTrigger unstyled value="activity" className="cea-tab">
              <Activity className="shrink-0" />
              Activity
            </TabsTrigger>
          </TabsList>
          <span className="ag-summary">
            <Clock aria-hidden="true" />
            <span>Last seen <strong>{timeAgo(agent.lastSeenAt)}</strong></span>
          </span>
        </div>

        <TabsContent value="overview" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <OverviewTab agent={agent} health={health} onRemoveA2a={() => setConfirmRemoveA2a(true)} sensSkills={sensSkills} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="access" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <CapabilityAccessTab
                agentId={agentId}
                agentName={agent.agentName || ""}
              />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="analytics" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <AnalyticsTab agentId={agentId} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="sessions" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <SessionsTab agentId={agentId} currentAgent={agent} />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="activity" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <ActivityTab agent={agent} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={confirmDeprovision} onOpenChange={(v) => !deprovisioning && setConfirmDeprovision(v)}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Trash2 />
              </span>
              <div>
                <DialogTitle>Deprovision agent?</DialogTitle>
                <DialogDescription>
                  This is a <strong>terminal</strong> action. All active sessions are killed and{" "}
                  <strong>{agent.agentName || "this agent"}</strong> can never reconnect. This cannot be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => setConfirmDeprovision(false)}
              disabled={deprovisioning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeprovision}
              disabled={deprovisioning}
              className="ag-danger-button"
            >
              {deprovisioning ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
              Deprovision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmRemoveA2a} onOpenChange={(v) => !removingA2a && setConfirmRemoveA2a(v)}>
        <DialogContent className="management-dialog ag-dialog ag-confirm">
          <DialogHeader className="management-dialog-header">
            <div className="management-dialog-heading">
              <span className="management-dialog-icon" data-tone="danger" aria-hidden="true">
                <Link2Off />
              </span>
              <div>
                <DialogTitle>Remove A2A registration?</DialogTitle>
                <DialogDescription>
                  This removes <strong>{agent.agentName || "this agent"}</strong> and its skills
                  from the gateway. Other agents will no longer be able to invoke it over A2A.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="management-dialog-footer">
            <Button
              variant="outline"
              onClick={() => setConfirmRemoveA2a(false)}
              disabled={removingA2a}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemoveA2a}
              disabled={removingA2a}
              className="ag-danger-button"
            >
              {removingA2a ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Link2Off className="w-3.5 h-3.5 mr-1.5" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function RegisterA2ADialog({
  open,
  onClose,
  onRegistered,
}: {
  open: boolean;
  onClose: () => void;
  onRegistered: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setBaseUrl("");
    }
  }, [open]);

  const handleRegister = async () => {
    const n = name.trim();
    const u = baseUrl.trim();
    if (!n || !u) {
      toast({ title: "Name and base URL are required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await GatewayA2AApi.register({ name: n, baseUrl: u });
      toast({
        title: `Agent '${res?.agentName || n}' registered${res?.skillsRegistered != null ? ` · ${res.skillsRegistered} skill(s)` : ""}`,
      });
      onClose();
      onRegistered();
    } catch (e: any) {
      toast({ title: "Registration failed", description: apiError(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-md">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <Share2 />
            </span>
            <div>
              <DialogTitle>Register A2A Agent</DialogTitle>
              <DialogDescription>
                The gateway fetches the Agent Card at{" "}
                <code className="text-primary/80 font-mono text-[11px]">/.well-known/agent-card.json</code> and registers each skill as an A2A capability.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Agent Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. billing" autoFocus />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Base URL</label>
              <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://billing.internal.example.com" className="font-mono" />
            </div>
          </div>
        </div>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleRegister} disabled={saving} className="endpoint-primary-action">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Register
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportA2ADialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}) {
  const { toast } = useToast();
  const [raw, setRaw] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setRaw("");
  }, [open]);

  const handleImport = async () => {
    const text = raw.trim();
    if (!text) {
      toast({ title: "Paste Agent Card JSON first", variant: "destructive" });
      return;
    }
    let parsed: any;
    try {
      parsed = JSON.parse(text);
    } catch (e: any) {
      toast({ title: "Invalid JSON", description: e?.message || String(e), variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await GatewayA2AApi.importCards(parsed);
      const total = res?.total ?? 0;
      const ok = res?.registered ?? 0;
      const failed = Array.isArray(res?.results) ? res.results.filter((r: any) => !r.registered) : [];
      toast({
        title: `Imported ${ok} of ${total} agent(s)`,
        description: failed.length ? failed.map((f: any) => f.error || "unknown error").join(" · ") : undefined,
        variant: failed.length && !ok ? "destructive" : undefined,
      });
      onClose();
      onImported();
    } catch (e: any) {
      toast({ title: "Import failed", description: apiError(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && !v && onClose()}>
      <DialogContent className="management-dialog ag-dialog sm:max-w-lg">
        <DialogHeader className="management-dialog-header">
          <div className="management-dialog-heading">
            <span className="management-dialog-icon" aria-hidden="true">
              <Upload />
            </span>
            <div>
              <DialogTitle>Import Agents from JSON</DialogTitle>
              <DialogDescription>
                Paste one Agent Card (a JSON object) or an array of cards — the same shape agents publish at{" "}
                <code className="text-primary/80 font-mono text-[11px]">/.well-known/agent-card.json</code>. Each card's own{" "}
                <code className="text-primary/80 font-mono text-[11px]">url</code> is its endpoint; a base URL already registered to another agent is rejected.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="management-dialog-body">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            rows={12}
            spellCheck={false}
            autoFocus
            placeholder={'{ "name": "billing", "url": "https://billing.internal.example.com", "skills": [ ... ] }'}
            className="w-full px-3 py-2.5 rounded-lg border border-border ag-surface text-xs font-mono text-foreground/90 placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 resize-y"
          />
        </div>
        <DialogFooter className="management-dialog-footer">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={saving} className="endpoint-primary-action">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function GatewayAgentsPage() {
  const { toast } = useToast();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [sensAgents, setSensAgents] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<AgentSummary>({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<"list" | "detail">("list");
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [activeListTab, setActiveListTab] = useState<"agents" | "live">("agents");
  const [agentSearch, setAgentSearch] = useState("");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const refreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadAgents = useCallback(async () => {
    try {
      const [list, s, sens] = await Promise.all([
        GatewayAgentsApi.list(),
        GatewayAgentsApi.summary(),
        GatewayPostProcessorApi.entitySensitivity().catch(() => ({})),
      ]);
      setAgents(Array.isArray(list) ? list : []);
      setSummary(s || {});
      setSensAgents(sens?.agents || {});
    } catch (e: any) {
      toast({
        title: "Failed to load agents",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAgents();
    refreshRef.current = setInterval(() => {
      if (activeListTab === "agents") loadAgents();
    }, 10000);
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [loadAgents, activeListTab]);

  useEffect(() => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    if (activeListTab === "agents" && currentView === "list") {
      refreshRef.current = setInterval(loadAgents, 10000);
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, [activeListTab, currentView, loadAgents]);

  const handleSelectAgent = (id: string) => {
    if (refreshRef.current) clearInterval(refreshRef.current);
    setSelectedAgentId(id);
    setCurrentView("detail");
  };

  const handleBack = () => {
    setCurrentView("list");
    setSelectedAgentId(null);
    setActiveListTab("agents");
    loadAgents();
    refreshRef.current = setInterval(loadAgents, 10000);
  };

  if (currentView === "detail" && selectedAgentId) {
    return (
      <div className="ag-page">
        <AgentDetailView agentId={selectedAgentId} onBack={handleBack} />
      </div>
    );
  }

  const pendingCount = summary.pendingApproval || 0;

  return (
    <div className="ag-page">
      <RegisterA2ADialog open={registerOpen} onClose={() => setRegisterOpen(false)} onRegistered={loadAgents} />
      <ImportA2ADialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadAgents} />

      <Tabs
        value={activeListTab}
        onValueChange={(v) => setActiveListTab(v as "agents" | "live")}
        className="ag-tabs-root"
      >
        <div className="ag-card ag-toolbar ag-nav">
          <TabsList unstyled aria-label="Agent views" className="cea-tabs ag-tabs">
            <TabsTrigger unstyled value="agents" className="cea-tab">
              <Bot className="shrink-0" />
              Agents
              {pendingCount > 0 && <span className="ag-tab-count" data-tone="amber">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger unstyled value="live" className="cea-tab">
              <Activity className="shrink-0" />
              Live Sessions
              {(summary.connectedSessions ?? 0) > 0 && (
                <span className="ag-tab-count" data-tone="success">{summary.connectedSessions}</span>
              )}
            </TabsTrigger>
          </TabsList>
          <div className="ag-toolbar-group">
            {pendingCount > 0 && (
              <span className="ag-live" data-tone="amber">
                <span className="ag-live-dot animate-pulse" aria-hidden="true" />
                {pendingCount} pending approval
              </span>
            )}
            <Button
              variant="outline"
              className="ag-button no-default-hover-elevate"
              onClick={() => setRegisterOpen(true)}
              title="Register an agent-to-agent endpoint by name + base URL"
            >
              <Plus />
              Register A2A Agent
            </Button>
            <Button
              variant="outline"
              className="ag-button no-default-hover-elevate"
              onClick={() => setImportOpen(true)}
              title="Register one or many agents by pasting Agent Card JSON"
            >
              <Upload />
              Import JSON
            </Button>
            <Button
              variant="outline"
              className="ag-button no-default-hover-elevate"
              onClick={loadAgents}
              disabled={loading}
            >
              {loading ? <span className="ag-spinner animate-spin" aria-hidden="true" /> : <RefreshCw />}
              Refresh
            </Button>
          </div>
        </div>

        <div className="ag-stat-grid">
          <GatewayStatCard
            icon={<Bot />}
            label="Total Agents"
            value={summary.totalAgents ?? 0}
            colorClass="text-primary"
          />
          <GatewayStatCard
            icon={<Activity />}
            label="Active"
            value={summary.activeAgents ?? 0}
            colorClass="text-emerald-600 dark:text-emerald-400"
          />
          <GatewayStatCard
            icon={<Users />}
            label="Connected Sessions"
            value={summary.connectedSessions ?? 0}
            colorClass="text-violet-600 dark:text-violet-400"
          />
          <GatewayStatCard
            icon={<TrendingUp />}
            label="Total Requests"
            value={summary.totalRequests ?? 0}
            colorClass="text-sky-600 dark:text-sky-400"
          />
        </div>

        <TabsContent value="agents" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            {!loading && agents.length > 0 && (
              <div className="ag-card-header ag-card-header-bordered">
                <div className="ag-card-heading">
                  <span className="ag-heading-icon" aria-hidden="true">
                    <Bot />
                  </span>
                  <div className="min-w-0">
                    <h2 className="ag-card-title">Discovered Agents</h2>
                    <p className="ag-card-description">{agents.length} agent{agents.length !== 1 ? "s" : ""} registered with the gateway</p>
                  </div>
                </div>
                <div className="ag-search ag-search-wide">
                  <Search aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Search agents by name, version..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className="ag-scroll">
              {loading ? (
                <LoadingSpinner text="Loading agents…" />
              ) : agents.length === 0 ? (
                <EmptyState
                  message="No agents discovered yet"
                  sub="Agents are auto-discovered when they connect via MCP."
                />
              ) : (
                <div className="ag-server-list">
                  {(() => {
                    if (!agentSearch.trim()) return agents;
                    const q = agentSearch.toLowerCase().trim();
                    const startsWith: typeof agents = [];
                    const contains: typeof agents = [];
                    for (const a of agents) {
                      const name = (a.agentName || "").toLowerCase();
                      if (name.startsWith(q)) startsWith.push(a);
                      else if (name.includes(q) || (a.agentVersion || "").toLowerCase().includes(q)) contains.push(a);
                    }
                    return [...startsWith, ...contains];
                  })()
                    .map((agent) => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        onSelect={handleSelectAgent}
                        sensitivity={sensAgents[agent.id]}
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="live" className="ag-panel mt-0">
          <div className="ag-card ag-content">
            <div className="ag-scroll">
              <LiveSessionsTab />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
